import { useEffect } from 'react'

import {
  FormWithSimpleMaterializedFormsFragment,
  TrainingFormWithTrainingDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formFromData, useCreateForm } from 'modules/forms/hooks/useForms'

type Data = {
  trainingId: string
  formId: string
}

export const SaveAsTemplateActivity: ActivityComponent<Data> = ({ trainingId, formId }) => {
  const { open, closeActivity } = useActivity()
  const { company, access } = useCurrentSession()
  const { data } = useQuery(TrainingFormWithTrainingDocument, {
    variables: { id: formId ?? '', trainingId: Number(trainingId), companyId: company.id },
    skip: !open,
  })
  const { createForm } = useCreateForm()

  const trainingForm: FormWithSimpleMaterializedFormsFragment | undefined =
    data?.forms.form ?? undefined

  useEffect(() => {
    if (!open || !trainingForm) return

    const onSaveAsTemplate = async (formToSave: FormWithSimpleMaterializedFormsFragment) => {
      if (formToSave) {
        const formElements = formToSave?.elements ?? []
        // setting the 'resourceId' to null makes the form to be saved as template
        const formInstance = formFromData({
          ...formToSave,
          description: formToSave.description ?? undefined,
          resourceId: undefined,
          status: 'active',
          elements: formElements.map((element) => ({
            ...element,
            description: element.description ?? '',
          })),
        })
        const result = await createForm(formInstance, company.id, access.id)
        if (result.success && result.data?.form?.id) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('trainings.surveys.create.dropdown.template_success'),
          })
          closeActivity()
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.surveys.create.dropdown.template_error'),
          })
          closeActivity()
        }
        return result
      }
    }

    onSaveAsTemplate(trainingForm)
  }, [open, trainingForm, company.id, access.id, createForm, closeActivity])

  return null
}
